import { type CreatePatientInput, type Pricing } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { formatMoney } from '#client/lib/format';
import { BillingSummary } from './BillingSummary';
import { PAYMENT_METHOD_OPTIONS } from '#client/lib/form-choices';

/** The charge fields a visit carries, whichever form registers it. */
export type BillingValues = Pick<
  CreatePatientInput,
  | 'discount_rupees'
  | 'discount_percentage'
  | 'tax_percentage'
  | 'other_charges'
  | 'other_charges_label'
  | 'payment_amount'
  | 'payment_method'
>;

interface BillingFieldsProps {
  values: BillingValues;
  onFieldsChange: (changes: Partial<BillingValues>) => void;
  errors: Partial<Record<string, string>>;
}

interface PatientBillingFieldsProps extends BillingFieldsProps {
  pricing: Pricing;
  /** Already in the ledger for this visit — zero when registering. */
  alreadyPaid: number;
}

/**
 * What the visit is charged, and how it was paid.
 *
 * Shared by the counter's patient form and registration from a test request,
 * so a visit is billed by the same rules whichever door the patient came in by.
 *
 * The fields are laid out in the order the arithmetic runs — discount, tax,
 * extra charge, then what was handed over — so the summary underneath reads as
 * a continuation of the inputs above rather than a separate claim.
 */
export function PatientBillingFields({
  values,
  onFieldsChange,
  errors,
  pricing,
  alreadyPaid,
}: PatientBillingFieldsProps) {
  const fields = { values, onFieldsChange, errors };
  return (
    <Card title="Billing">
      <div className="flex flex-col gap-4">
        <DiscountFields {...fields} />
        <OtherChargeFields {...fields} />
        <PaymentFields {...fields} alreadyPaid={alreadyPaid} />

        <BillingSummary
          pricing={pricing}
          discountRupees={values.discount_rupees}
          discountPercentage={values.discount_percentage}
          taxPercentage={values.tax_percentage}
          otherChargesLabel={values.other_charges_label}
          showStatus
        />
      </div>
    </Card>
  );
}

function DiscountFields({ values, onFieldsChange, errors }: BillingFieldsProps) {
  // The two discounts are mutually exclusive. `calculatePricing` lets the
  // percentage win if both somehow arrive; clearing the other here means the
  // operator never sees that rule fire.
  const setDiscountPercentage = (percentage: number) =>
    onFieldsChange({ discount_percentage: percentage, discount_rupees: 0 });
  const setDiscountRupees = (rupees: number) =>
    onFieldsChange({ discount_rupees: rupees, discount_percentage: 0 });

  return (
    <div className="c-form-grid">
      <NumberField
        label="Discount (Rs)"
        min={0}
        step={0.01}
        value={values.discount_rupees}
        error={errors.discount_rupees}
        onChange={(rupees) => setDiscountRupees(rupees)}
        helpText="Clears the percentage discount"
      />
      <NumberField
        label="Discount (%)"
        min={0}
        max={100}
        step={0.01}
        value={values.discount_percentage}
        error={errors.discount_percentage}
        onChange={(percentage) => setDiscountPercentage(percentage)}
        helpText="Clears the rupee discount"
      />
      <NumberField
        label="Sales tax (%)"
        min={0}
        max={100}
        step={0.01}
        value={values.tax_percentage}
        error={errors.tax_percentage}
        onChange={(percentage) => onFieldsChange({ tax_percentage: percentage })}
        helpText="Charged on the amount after discount"
      />
    </div>
  );
}

function OtherChargeFields({ values, onFieldsChange, errors }: BillingFieldsProps) {
  return (
    <div className="c-form-grid">
      <NumberField
        label="Other charges"
        min={0}
        step={0.01}
        value={values.other_charges}
        error={errors.other_charges}
        onChange={(rupees) => onFieldsChange({ other_charges: rupees })}
        prefix="Rs"
      />
      <TextField
        label="What for"
        value={values.other_charges_label ?? ''}
        error={errors.other_charges_label}
        onChange={(event) => onFieldsChange({ other_charges_label: event.target.value })}
        // Printed on the slip beside the amount. An unexplained charge is
        // the line a patient queries at the counter.
        placeholder="Home collection, urgent, courier…"
        required={values.other_charges > 0}
      />
    </div>
  );
}

function PaymentFields({
  values,
  onFieldsChange,
  errors,
  alreadyPaid,
}: BillingFieldsProps & { alreadyPaid: number }) {
  return (
    <div className="c-form-grid">
      <NumberField
        label="Received now"
        min={0}
        step={0.01}
        value={values.payment_amount}
        error={errors.payment_amount}
        onChange={(amount) => {
          // Clear the method alongside, so a zeroed amount does not submit
          // a method for a payment that is no longer happening.
          onFieldsChange(
            amount > 0
              ? { payment_amount: amount }
              : { payment_amount: amount, payment_method: null },
          );
        }}
        prefix="Rs"
        // Added to what is already paid, never a replacement for it: a
        // payment is recorded against whoever is signed in, now.
        helpText={alreadyPaid > 0 ? `${formatMoney(alreadyPaid)} already paid` : undefined}
      />
      {/* Disabled rather than hidden so the field does not appear and
          vanish as the amount is typed into. Required once money changes
          hands — the drawer is counted by method. */}
      <Select
        label="Payment method"
        options={PAYMENT_METHOD_OPTIONS}
        value={values.payment_method ?? ''}
        error={errors.payment_method}
        disabled={values.payment_amount <= 0}
        required={values.payment_amount > 0}
        placeholder="Choose a method"
        helpText={values.payment_amount > 0 ? undefined : 'Recorded once something is received'}
        onChange={(choice) => onFieldsChange({ payment_method: choice || null })}
      />
    </div>
  );
}
